import React from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import moment from 'moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@components/base'
import { ORDER_SUMMARY_DATE_FORMAT } from '@src/config/momentFormat'
import { formatCurrencyAmount } from '../../utils/utils'

export interface OrderSummaryProps {
  title?: string
  price: number
  estimatedTax: number
  discount: number
  discountLabel?: string
  discountDisplayValue?: string
  chargeDate: string
  disclaimer?: string
  showEstimatedTax?: boolean
  onlyShowDateCharged?: boolean
  style?: StyleProp<ViewStyle>
}

export const OrderSummary = ({
  title = 'Order Summary',
  price,
  discount,
  discountLabel = 'Discount',
  discountDisplayValue,
  estimatedTax,
  chargeDate,
  disclaimer,
  showEstimatedTax = true,
  onlyShowDateCharged = false,
  style,
}: OrderSummaryProps) => {
  const styles = useStyleSheet(themedStyle)
  const total = formatCurrencyAmount(Math.max(price - discount + estimatedTax, 0))
  const chargeDateMoment = moment(chargeDate, ORDER_SUMMARY_DATE_FORMAT)
  const chargeToday = chargeDateMoment.isSame(moment(), 'day')
  const todayTotal = chargeToday ? `$${total}` : '$0'
  const showTodayTotal = chargeToday || !onlyShowDateCharged
  const discountFormatted = formatCurrencyAmount(Math.min(price, discount))

  return (
    <View style={style}>
      <Text type="title-3" style={styles.title}>
        {title}
      </Text>
      <View style={styles.row}>
        <Text type="regular">Order Total</Text>
        <Text type="regular">${formatCurrencyAmount(price)}</Text>
      </View>
      {discount > 0 && (
        <View style={styles.row}>
          <Text type="regular">{discountLabel}</Text>
          <Text type="regular">{discountDisplayValue ?? `-$${discountFormatted}`}</Text>
        </View>
      )}
      {showEstimatedTax && (
        <View style={styles.row}>
          <Text type="regular">Estimated Tax</Text>
          <Text type="regular">${formatCurrencyAmount(estimatedTax)}</Text>
        </View>
      )}
      {showTodayTotal && (
        <View style={styles.row}>
          <View>
            <Text type="regular" bold>
              Amount charged today
            </Text>
            {chargeToday && !!disclaimer && (
              <Text type="small" style={styles.disclaimerText}>
                {disclaimer}
              </Text>
            )}
          </View>
          <Text type="regular" bold>
            {todayTotal}
          </Text>
        </View>
      )}
      {!chargeToday && (
        <View style={styles.row}>
          <View>
            <Text type="regular" bold>
              Amount charged on {chargeDate}
            </Text>
            {!!disclaimer && (
              <Text type="small" style={styles.disclaimerText}>
                {disclaimer}
              </Text>
            )}
          </View>
          <Text type="regular" bold>
            ${total}
          </Text>
        </View>
      )}
    </View>
  )
}

const themedStyle = StyleService.create({
  title: {
    marginTop: 24,
  },
  row: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  disclaimerText: {
    color: 'theme.text.secondary',
  },
})
